import { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCwIcon, TriangleAlertIcon, WalletIcon } from "lucide-react";
import { api } from "@/client/lib";
import { useAuth } from "@/client/lib/auth-context";
import { cn } from "@/client/utils/index";
import type { LiveEngineState, StrategyMode } from "@/services";
import type { ExitReason, LegState, OrderState, PositionState, StrategyStatus } from "@/engine/types";
import { Alert, AlertDescription, AlertTitle } from "@/client/components/base/alert";
import { Badge } from "@/client/components/base/badge";
import { Button } from "@/client/components/base/button";
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@/client/components/base/card";
import { Separator } from "@/client/components/base/separator";
import { Spinner } from "@/client/components/base/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/client/components/base/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/client/components/base/tabs";

type BadgeVariant = "default" | "secondary" | "destructive" | "outline";

type StrategyGroup = {
    legs: LegState[];
    positions: PositionState[];
    orders: OrderState[];
};

const MODE_LABELS: Record<StrategyMode, string> = {
    LIVE: "Live",
    FORWARD_TEST: "Forward test",
};

const RUNNING_STATUSES: StrategyStatus[] = ["CREATED", "ACTIVE", "FLAT", "EXITING"];

const isRunning = (status: StrategyStatus) => RUNNING_STATUSES.includes(status);

const strategyVariant = (status: StrategyStatus): BadgeVariant => {
    switch (status) {
        case "ACTIVE":
            return "default";
        case "FLAT":
        case "EXITING":
            return "secondary";
        case "FAILED":
            return "destructive";
        default:
            return "outline";
    }
};

const statusVariant = (status: string): BadgeVariant => {
    if (status === "ACTIVE" || status === "ENTERED" || status === "COMPLETE") return "default";
    if (status === "FAILED" || status === "REJECTED") return "destructive";
    if (status === "EXITED" || status === "EXHAUSTED" || status === "SKIPPED" || status === "CANCELLED") return "outline";
    return "secondary";
};

const pnlClass = (value: number) =>
    value > 0 ? "text-emerald-600 dark:text-emerald-500" : value < 0 ? "text-red-600 dark:text-red-500" : "text-muted-foreground";

const formatNumber = (value: number, digits = 2) =>
    value.toLocaleString("en-IN", { minimumFractionDigits: digits, maximumFractionDigits: digits });

const formatCurrency = (value: number) =>
    value.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });

const formatDateTime = (value?: Date | string | null): string => {
    if (!value) return "—";
    const date = new Date(value as string);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });
};

const reasonLabel = (reason?: ExitReason) => (reason ? reason.replaceAll("_", " ").toLowerCase() : "—");

const directionLabel = (quantity: number) => (quantity >= 0 ? "LONG" : "SHORT");

const PositionsPage = () => {
    const { token, logout } = useAuth();
    const [state, setState] = useState<LiveEngineState | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

    const load = useCallback(
        async (silent = false) => {
            if (!token) return;
            if (!silent) setIsLoading(true);

            const { data, error } = await api.engine.live.get({
                headers: { authorization: `Bearer ${token}` },
            });

            if (error) {
                if (error.status === 401) {
                    logout();
                    return;
                }
                setError(`Failed to load positions (${error.status})`);
                setIsLoading(false);
                return;
            }

            setState(data && "strategies" in data ? data : null);
            setError(null);
            setUpdatedAt(new Date());
            setIsLoading(false);
        },
        [token, logout]
    );

    useEffect(() => {
        if (!token) return;
        void load();
        const timer = setInterval(() => void load(true), 30_000);
        return () => clearInterval(timer);
    }, [token, load]);

    const groups = useMemo(() => {
        const map = new Map<string, StrategyGroup>();
        if (!state) return map;

        for (const strategy of state.strategies) {
            map.set(strategy.strategyId, { legs: [], positions: [], orders: [] });
        }
        for (const leg of state.legs) map.get(leg.strategyId)?.legs.push(leg);
        for (const position of state.positions) map.get(position.strategyId)?.positions.push(position);
        for (const order of state.orders) map.get(order.strategyId)?.orders.push(order);

        return map;
    }, [state]);

    const strategies = state?.strategies ?? [];
    const byMode = useMemo(
        () => ({
            LIVE: strategies.filter(strategy => strategy.mode === "LIVE"),
            FORWARD_TEST: strategies.filter(strategy => strategy.mode === "FORWARD_TEST"),
        }),
        [strategies]
    );

    const activeStrategies = strategies.filter(strategy => isRunning(strategy.status)).length;
    const openPositions = (state?.positions ?? []).filter(
        position => position.status === "ENTERED" || position.status === "EXITING" || position.status === "PARTIAL"
    ).length;
    const netPnl = (state?.positions ?? []).reduce((sum, position) => sum + position.realizedPnl - position.charges, 0);

    const renderStrategies = (list: typeof strategies) => {
        if (list.length === 0) {
            return (
                <Card>
                    <CardContent className="flex flex-col items-center justify-center gap-2 py-12 text-center text-muted-foreground">
                        <WalletIcon className="size-6" aria-hidden="true" />
                        <p className="text-sm">No strategies in this mode today.</p>
                    </CardContent>
                </Card>
            );
        }

        return (
            <div className="flex flex-col gap-4">
                {list.map(strategy => {
                    const group = groups.get(strategy.strategyId) ?? { legs: [], positions: [], orders: [] };
                    const net = strategy.realizedPnl - strategy.charges;

                    return (
                        <Card key={strategy.strategyId} size="sm">
                            <CardHeader className="border-b">
                                <CardTitle className="flex items-center gap-2">
                                    {strategy.signalId || "strategy"}
                                    <Badge variant="outline">{MODE_LABELS[strategy.mode]}</Badge>
                                    <Badge variant={strategyVariant(strategy.status)}>{strategy.status}</Badge>
                                </CardTitle>
                                <CardDescription className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                    <span>{strategy.underlying}</span>
                                    <span aria-hidden>·</span>
                                    <span className="font-mono text-xs">{strategy.strategyId}</span>
                                    <span aria-hidden>·</span>
                                    <span>entered {formatDateTime(strategy.entryTime)}</span>
                                    {strategy.exitTime && (
                                        <>
                                            <span aria-hidden>·</span>
                                            <span>
                                                exited {formatDateTime(strategy.exitTime)} ({reasonLabel(strategy.exitReason)})
                                            </span>
                                        </>
                                    )}
                                </CardDescription>
                                <CardAction className="text-right">
                                    <p className="text-xs text-muted-foreground">Net PnL</p>
                                    <p className={cn("text-base font-semibold tabular-nums", pnlClass(net))}>{formatCurrency(net)}</p>
                                </CardAction>
                            </CardHeader>

                            <CardContent>
                                <Tabs defaultValue="positions">
                                    <TabsList variant="line">
                                        <TabsTrigger value="legs">Legs ({group.legs.length})</TabsTrigger>
                                        <TabsTrigger value="positions">Positions ({group.positions.length})</TabsTrigger>
                                        <TabsTrigger value="orders">Orders ({group.orders.length})</TabsTrigger>
                                    </TabsList>

                                    <TabsContent value="legs" className="pt-2">
                                        <LegsTable legs={group.legs} />
                                    </TabsContent>
                                    <TabsContent value="positions" className="pt-2">
                                        <PositionsTable positions={group.positions} />
                                    </TabsContent>
                                    <TabsContent value="orders" className="pt-2">
                                        <OrdersTable orders={group.orders} />
                                    </TabsContent>
                                </Tabs>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>
        );
    };

    return (
        <main className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-4 sm:p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight">Live positions</h1>
                    <p className="text-sm text-muted-foreground">
                        Strategies active now or earlier today, across live and forward-test, with their legs, positions and orders.
                        {updatedAt && <> Updated {formatDateTime(updatedAt)}.</>}
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => void load()} disabled={isLoading}>
                    <RefreshCwIcon data-icon="inline-start" />
                    Refresh
                </Button>
            </div>

            {!token ? (
                <Alert variant="destructive">
                    <TriangleAlertIcon aria-hidden="true" />
                    <AlertTitle>Not signed in</AlertTitle>
                    <AlertDescription>Sign in to view live engine positions.</AlertDescription>
                </Alert>
            ) : isLoading ? (
                <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
                    <Spinner />
                    Loading positions...
                </div>
            ) : error ? (
                <Alert variant="destructive">
                    <TriangleAlertIcon aria-hidden="true" />
                    <AlertTitle>Something went wrong</AlertTitle>
                    <AlertDescription className="flex items-center justify-between gap-4">
                        {error}
                        <Button variant="outline" size="sm" onClick={() => void load()}>
                            Retry
                        </Button>
                    </AlertDescription>
                </Alert>
            ) : (
                <>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                        <StatCard label="Strategies" value={String(strategies.length)} detail={`${activeStrategies} running`} />
                        <StatCard label="Open positions" value={String(openPositions)} detail={`${state?.positions.length ?? 0} total today`} />
                        <StatCard label="Orders" value={String(state?.orders.length ?? 0)} detail="entry + exit" />
                        <StatCard label="Net PnL" value={formatCurrency(netPnl)} detail="realized − charges" tone={netPnl} />
                    </div>

                    <Separator />

                    <Tabs defaultValue="LIVE" className="w-full">
                        <TabsList>
                            <TabsTrigger value="LIVE">
                                Live
                                <Badge variant="secondary">{byMode.LIVE.length}</Badge>
                            </TabsTrigger>
                            <TabsTrigger value="FORWARD_TEST">
                                Forward test
                                <Badge variant="secondary">{byMode.FORWARD_TEST.length}</Badge>
                            </TabsTrigger>
                        </TabsList>
                        <TabsContent value="LIVE" className="pt-4">
                            {renderStrategies(byMode.LIVE)}
                        </TabsContent>
                        <TabsContent value="FORWARD_TEST" className="pt-4">
                            {renderStrategies(byMode.FORWARD_TEST)}
                        </TabsContent>
                    </Tabs>
                </>
            )}
        </main>
    );
};

const StatCard = ({ label, value, detail, tone }: { label: string; value: string; detail: string; tone?: number }) => (
    <Card size="sm">
        <CardContent className="flex flex-col gap-1">
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className={cn("text-xl font-semibold tabular-nums", tone !== undefined && pnlClass(tone))}>{value}</p>
            <p className="text-xs text-muted-foreground">{detail}</p>
        </CardContent>
    </Card>
);

const LegsTable = ({ legs }: { legs: LegState[] }) =>
    legs.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">No legs.</p>
    ) : (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>Leg</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Attempts</TableHead>
                    <TableHead className="text-right">Losses</TableHead>
                    <TableHead>Last exit</TableHead>
                    <TableHead className="text-right">PnL</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {legs.map(leg => {
                    const net = leg.realizedPnl - leg.charges;
                    return (
                        <TableRow key={leg.legId}>
                            <TableCell className="font-medium">{leg.legKey}</TableCell>
                            <TableCell>
                                <Badge variant={statusVariant(leg.status)}>{leg.status}</Badge>
                            </TableCell>
                            <TableCell className="text-right tabular-nums">{leg.attemptCount}</TableCell>
                            <TableCell className="text-right tabular-nums">{leg.consecutiveLosses}</TableCell>
                            <TableCell className="text-muted-foreground">
                                {leg.lastExit ? `${reasonLabel(leg.lastExit.reason)} @ ${formatNumber(leg.lastExit.price)}` : "—"}
                            </TableCell>
                            <TableCell className={cn("text-right tabular-nums", pnlClass(net))}>{formatCurrency(net)}</TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );

const PositionsTable = ({ positions }: { positions: PositionState[] }) =>
    positions.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">No positions yet.</p>
    ) : (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>Leg</TableHead>
                    <TableHead>Symbol</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Side</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Filled</TableHead>
                    <TableHead className="text-right">Entry</TableHead>
                    <TableHead className="text-right">Exit</TableHead>
                    <TableHead>Entry time</TableHead>
                    <TableHead>Exit time</TableHead>
                    <TableHead>Exit reason</TableHead>
                    <TableHead className="text-right">PnL</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {positions.map(position => {
                    const net = position.realizedPnl - position.charges;
                    return (
                        <TableRow key={position.positionId}>
                            <TableCell className="font-medium">
                                {position.legKey}
                                <span className="ml-1 text-xs text-muted-foreground">#{position.attempt}</span>
                            </TableCell>
                            <TableCell className="font-mono text-xs">{position.symbol}</TableCell>
                            <TableCell>
                                <Badge variant={statusVariant(position.status)}>{position.status}</Badge>
                            </TableCell>
                            <TableCell
                                className={cn(
                                    "text-right text-xs font-medium",
                                    position.quantity >= 0 ? "text-emerald-600 dark:text-emerald-500" : "text-red-600 dark:text-red-500"
                                )}
                            >
                                {directionLabel(position.quantity)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">{formatNumber(position.quantity, 0)}</TableCell>
                            <TableCell className="text-right tabular-nums">{formatNumber(position.filledQuantity, 0)}</TableCell>
                            <TableCell className="text-right tabular-nums">
                                {position.entryPrice !== undefined ? formatNumber(position.entryPrice) : "—"}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                                {position.exitPrice !== undefined ? formatNumber(position.exitPrice) : "—"}
                            </TableCell>
                            <TableCell className="text-muted-foreground">{formatDateTime(position.entryTime)}</TableCell>
                            <TableCell className="text-muted-foreground">{formatDateTime(position.exitTime)}</TableCell>
                            <TableCell className="text-muted-foreground">{reasonLabel(position.exitReason)}</TableCell>
                            <TableCell className={cn("text-right tabular-nums", pnlClass(net))}>{formatCurrency(net)}</TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );

const OrdersTable = ({ orders }: { orders: OrderState[] }) =>
    orders.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">No orders.</p>
    ) : (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>Placed</TableHead>
                    <TableHead>Leg</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Side</TableHead>
                    <TableHead>Symbol</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Filled</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                    <TableHead className="text-right">Avg</TableHead>
                    <TableHead>Filled at</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {orders.map(order => (
                    <TableRow key={order.orderId}>
                        <TableCell className="text-muted-foreground">{formatDateTime(order.createdAt)}</TableCell>
                        <TableCell className="font-medium">{order.legKey}</TableCell>
                        <TableCell>
                            <Badge variant={order.action === "ENTRY" ? "secondary" : "outline"}>{order.action}</Badge>
                        </TableCell>
                        <TableCell
                            className={cn(
                                "text-xs font-medium",
                                order.side === "BUY" ? "text-emerald-600 dark:text-emerald-500" : "text-red-600 dark:text-red-500"
                            )}
                        >
                            {order.side}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{order.symbol}</TableCell>
                        <TableCell>
                            <Badge variant={statusVariant(order.status)}>{order.status}</Badge>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{formatNumber(order.quantity, 0)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatNumber(order.filledQuantity, 0)}</TableCell>
                        <TableCell className="text-right tabular-nums">{order.price !== undefined ? formatNumber(order.price) : "—"}</TableCell>
                        <TableCell className="text-right tabular-nums">{order.avgPrice !== undefined ? formatNumber(order.avgPrice) : "—"}</TableCell>
                        <TableCell className="text-muted-foreground">{formatDateTime(order.filledAt)}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );

export default PositionsPage;
